import React, { useCallback, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Form, ListGroup } from 'react-bootstrap';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import '../../../assets/styles/WorldSalesMap.css';
import { useFormat } from '../../../components/FormatContext';
import WorldMapLoader from './WorldMapLoader';
import { DASHBOARD_CHART_TYPES } from '../utils/dashboardConstants';
import { normalizeWorldMapData } from '../utils/dashboardHelpers';

const WorldSalesMap = ({ data, currencyCode }) => {
    const { baseFormatters, formatCurrency } = useFormat();
    const [selectedChart, setSelectedChart] = useState('sales');

    const selectedData = useMemo(() => {
        let rawData = [];

        switch (selectedChart) {
            case 'ordersQty':
                rawData = data?.OrderQtyData || [];
                break;
            case 'income':
                rawData = data?.IncomeData || [];
                break;
            case 'sales':
            default:
                rawData = data?.SalesData || [];
                break;
        }

        return normalizeWorldMapData(rawData);
    }, [data, selectedChart]);

    const handleChartSelect = useCallback((event) => {
        setSelectedChart(event.target.value);
    }, []);

    const option = useMemo(() => {
        return {
            backgroundColor: '#F3F4F6',
            tooltip: {
                trigger: 'item',
                borderColor: '#ccc',
                formatter: (params) => {
                    const value = params.value || 0;

                    if (value === 0) {
                        return `<strong>${params.name}</strong>`;
                    }

                    return selectedChart === 'ordersQty'
                        ? `<strong>${params.name}</strong><br/>${baseFormatters.number.plain.format(value)}`
                        : `<strong>${params.name}</strong><br/>${formatCurrency(value, currencyCode)}<br/>`;
                },
            },
            visualMap: {
                type: 'piecewise',
                pieces: [{ min: 1, max: 100000000, label: 'sales', color: '#5AC098' }],
                left: 'right',
                show: false,
                textStyle: { color: '#333' },
            },
            geo: {
                map: 'world',
                roam: true,
                zoom: 1.25,
                label: { show: false },
                itemStyle: {
                    areaColor: '#D8D8E0',
                    borderColor: '#fff',
                    borderWidth: 0.5,
                },
                emphasis: {
                    label: { show: false },
                    itemStyle: {
                        areaColor: '#5ac098c2',
                    },
                },
            },
            series: [
                {
                    type: 'map',
                    map: 'world',
                    geoIndex: 0,
                    data: selectedData,
                    name:
                        selectedChart === 'sales'
                            ? 'Sales'
                            : selectedChart === 'ordersQty'
                                ? 'Orders Qty'
                                : 'Income',
                    select: {
                        disabled: true,
                    },
                },
            ],
        };
    }, [baseFormatters.number.plain, formatCurrency, selectedChart, selectedData, currencyCode]);

    return (
        <Card className="shadow-sm p-1">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Sales Map</h5>

                <Form.Select
                    value={selectedChart}
                    onChange={handleChartSelect}
                    style={{ width: '150px' }}
                >
                    {DASHBOARD_CHART_TYPES.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </Form.Select>
            </Card.Header>

            <Card.Body>
                <WorldMapLoader>
                    <ReactECharts
                        option={option}
                        style={{ height: '180px', width: '100%' }}
                        opts={{ locale: navigator.language }}
                        notMerge
                    />
                </WorldMapLoader>
            </Card.Body>

            <Card.Footer>
                <OverlayScrollbarsComponent
                    options={{
                        scrollbars: {
                            theme: 'os-theme-custom',
                        },
                    }}
                    style={{ maxHeight: '153px' }}
                >
                    <ListGroup variant="flush">
                        {selectedData.map((item) => (
                            <ListGroup.Item
                                key={item.name}
                                className="d-flex justify-content-between align-items-center ps-0 fs-14"
                            >
                                <span>{item.name}</span>
                                <span>
                                    {selectedChart === 'ordersQty'
                                        ? baseFormatters.number.plain.format(item.value)
                                        : formatCurrency(item.value, currencyCode)}
                                </span>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </OverlayScrollbarsComponent>
            </Card.Footer>
        </Card>
    );
};

export default WorldSalesMap;